import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle, MessageCircle } from 'lucide-react';
import { useDemoConfig } from '../hooks/useDemoConfig';
import { demoConfigService } from '../services/demoConfig.service';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HEADER_HEIGHT = '4rem';

/**
 * Branded sales-demo landing page: a header that mimics the client's own
 * site chrome (logo + top-bar background color from the DemoConfig). The
 * "Start Chat" button deliberately does NOT reuse that color — it's picked
 * for header contrast, not button contrast (e.g. white reads fine as a
 * header background but disappears as a solid button). The button instead
 * keeps the platform's normal default styling, same as every Ellie button
 * (driven by the tenant's own whitelabel primary color), so it stays
 * legible regardless of what a client's brand color is. Clicking it mints
 * a real embed session via POST /api/v1/ext/demo-config/{id}/start and
 * drops the resulting URL straight into an <iframe> below the header — the
 * same mint-then-iframe shape a production customer embed uses, just
 * hosted here for demos.
 */
export default function EmbedDemo() {
  const { id } = useParams<{ id: string }>();
  const { data: config, isLoading, error } = useDemoConfig(id);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [chatUrl, setChatUrl] = useState<string | null>(null);

  const handleStartChat = async () => {
    if (!id) return;
    setStarting(true);
    setStartError(null);
    try {
      const session = await demoConfigService.startDemoSession(id);
      setChatUrl(session.url);
    } catch (err: any) {
      setStartError(err?.payload?.msg || err?.message || 'Failed to start chat');
    } finally {
      setStarting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">This demo isn't available</h2>
            <p className="text-sm text-muted-foreground">
              This link may have expired or been turned off.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandStyle = config.brand_color
    ? { backgroundColor: config.brand_color }
    : undefined;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header
        className="flex items-center gap-3 border-b px-6 shrink-0"
        style={{ height: HEADER_HEIGHT, ...brandStyle }}
      >
        {config.logo_url && (
          <img src={config.logo_url} alt={config.client_name} className="h-8" />
        )}
        <span className="text-lg font-semibold text-white">{config.client_name}</span>
      </header>

      {chatUrl ? (
        <iframe
          src={chatUrl}
          title="Chat"
          className="w-full flex-1 border-0"
          style={{ height: `calc(100vh - ${HEADER_HEIGHT})` }}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center space-y-4">
            <Button size="lg" onClick={handleStartChat} disabled={starting}>
              {starting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Start Chat
                </>
              )}
            </Button>
            {startError && (
              <p className="text-sm text-destructive">{startError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
